/**
 * Voltar para a aba revalida o que pode ter mudado fora dela.
 *
 * ── Por que isto passou a ser necessário ──────────────────────────────────
 *
 * O WhatsApp virou canal de ESCRITA: ele lança transação, marca lembrete como
 * pago, cria limite. A tela, até agora, só sabia do que acontecia dentro
 * dela — então ela marcava a conta como paga pelo celular, voltava para o
 * navegador aberto ao lado, e via o estado velho.
 *
 * O sintoma que chegou foi outro, mais estreito: "se eu mudar o status do
 * lembrete linkado, a tabela de transações tem que recarregar". A causa é
 * essa, e é mais larga que o sintoma.
 *
 * ── Por que um piso de tempo ──────────────────────────────────────────────
 *
 * `visibilitychange` dispara em toda troca de aba. Sem piso, alternar entre
 * duas abas viraria uma rajada de requisições — e o `pisoDeTamanho`/rate
 * limit do backend está lá justamente para que carga acidental não passe.
 * Alt-tab de dois segundos não muda dado; ir ao WhatsApp e voltar, sim.
 *
 * ── Por que `Date.now()` aqui é aceitável ─────────────────────────────────
 *
 * A armadilha 3 proíbe `new Date()` para decidir "hoje", porque o servidor é
 * UTC e o dia vira às 21h BRT. Aqui não se decide dia nenhum: mede-se um
 * intervalo, no relógio do navegador, contra ele mesmo. Fuso não participa.
 */

/** Quanto tempo fora da aba justifica ir buscar de novo. */
export const PISO_DE_AUSENCIA_MS = 10_000;

export interface Relogio {
  agora: () => number;
}

const relogioReal: Relogio = { agora: () => Date.now() };

/**
 * Liga o listener e devolve a função que desliga.
 *
 * `revalidar` só é chamada quando a aba VOLTA depois de um tempo relevante —
 * nunca quando ela some, e nunca em troca rápida.
 */
export const observarVoltaDaAba = (
  revalidar: () => void,
  opts: { piso?: number; relogio?: Relogio; alvo?: Document } = {},
): (() => void) => {
  const piso = opts.piso ?? PISO_DE_AUSENCIA_MS;
  const relogio = opts.relogio ?? relogioReal;
  const doc = opts.alvo ?? (typeof document !== 'undefined' ? document : null);
  if (!doc) return () => {};

  let escondidaDesde: number | null = null;

  const aoMudar = () => {
    if (doc.visibilityState === 'hidden') {
      escondidaDesde = relogio.agora();
      return;
    }
    // Voltou. Sem marca de saída (aba nova, primeiro foco) não há o que
    // revalidar: os dados acabaram de ser carregados.
    if (escondidaDesde === null) return;
    const fora = relogio.agora() - escondidaDesde;
    escondidaDesde = null;
    if (fora >= piso) revalidar();
  };

  doc.addEventListener('visibilitychange', aoMudar);
  return () => doc.removeEventListener('visibilitychange', aoMudar);
};
